import Modal from "patient-ping-remedy/packages/modal";
import {StandardTypography} from "patient-ping-remedy/packages/typography";
import Button, {StyleType} from "patient-ping-remedy/packages/button";
import {useContext} from "react";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {useMutation} from "@tanstack/react-query";
import {useActiveRoster} from "@/store/roster_store";
import {CareJourneyDetailsResponse, SealAppointmentRequest} from "@/api/dto/care-journey";
import {useAlertStore} from "@/store/alert_store";
import {ToastType} from "patient-ping-remedy/packages/toast";
import {FilterInvalidate} from "@common/FilterComponent";
import Helpers from "@/helpers/helpers";
import {useAuthStore} from "@/store/auth_store";
import {AxiosError} from "axios";
import {css} from "@emotion/css";

type Props = {
    open: boolean;
    close: () => void;
    careJourneyDetails: CareJourneyDetailsResponse;
    invalidate: FilterInvalidate;
}

const SchedulingSealModal = (props: Props) => {
  const {carecoApi} = useContext(CarecoApiContext);
  const currentRoster = useActiveRoster();
  const {currentUser} = useAuthStore();
  const {addAlert} = useAlertStore();

  const sealed = !!props.careJourneyDetails.orchestrationEngineDetails?.appointmentSealed;

  const {mutate, isPending} = useMutation({
    mutationFn: async (request: SealAppointmentRequest) =>
        carecoApi!.sealAppointment(props.careJourneyDetails.careJourneyInstanceId, currentRoster!.id, request),
    onSuccess: (request: SealAppointmentRequest) => {
      addAlert({
        content: request.sealed ? 'Task is marked as Done scheduling successfully.'
          : 'Reopened scheduling for the task successfully.',
        type: ToastType.SUCCESS
      });
      props.invalidate();
      props.close();
    },
    onError: (error : AxiosError, request) => {
      Helpers.alertOnError(
        `${request.sealed ? 'Failed to mark task as Done scheduling.' : 'Failed to reopen scheduling for the task.'} 
        ${Helpers.traceId(error)}`
        , error);
    },
  });

  const handleSubmit = () => {
    const request: SealAppointmentRequest = {
      journey_id: props.careJourneyDetails.careJourneyInstanceId,
      sealed_by: currentUser!.id,
      sealed: !sealed,
    };

    mutate(request);
  };

  return <Modal
    id={"scheduling-seal-modal"}
    headerText={sealed ? "Reopen for scheduling" : "Mark as Done scheduling?"}
    isOpen={props.open}
    className={css({width: '90rem'})}
    buttons={[
      <Button
        id={"scheduling-seal-cancel-button"}
        key={"scheduling-seal-cancel-button"}
        onClick={props.close}
        styleType={StyleType.TERTIARY}
      >
        Cancel
      </Button>,
      <Button
        id={"scheduling-seal-submit-button"}
        key={"scheduling-seal-submit-button"}
        onClick={handleSubmit}
        disabled={isPending}
      >
        {sealed ? 'Reopen scheduling' : 'Done scheduling'}
      </Button>
    ]}
  >
    {sealed ?
      <StandardTypography>
      Reopening scheduling will allow you to add new appointments. Any existing appointments will not be affected.
      </StandardTypography> :
      <StandardTypography>
      After you mark as Done scheduling, you won't be able to add any more appointments.
      Any appointments already in progress will continue as usual. <br/>
      Click Reopen scheduling at any time if you need to add more appointments.
      </StandardTypography>
    }
  </Modal>;
};

export default SchedulingSealModal;
